"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronUp,
  Coins,
  Heart,
  Loader2,
  MapPin,
  Settings2,
  Shield,
  User,
  X,
} from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";

import techficientService, {
  type TechficientQuoteRequestPayload,
  type TechficientRunQuoteResponse,
} from "@/app/_module/services/techficientService";

import type { QuoteFormPreset, QuoteFormResult } from "./types";
import { CompulifeQuoteForm } from "@/app/(authenticated)/leads/_modules/components/CompulifeQuoteForm";
import CompulifeQuoteResults from "@/app/_module/components/insurabot-cards/CompulifeQuoteResults";

interface QuoteFormProps {
  /**
   * Pre-fill values — usually derived from a lead row via `leadToPreset`.
   * Pass {} to render an empty form.
   */
  preset?: QuoteFormPreset;
  /**
   * Optional callback invoked after a successful quote run. Both the request
   * and response are bubbled up so the parent can decide what to do next
   * (e.g. scroll, persist, attach a Cases follow-up card).
   */
  onQuoted?: (result: QuoteFormResult) => void;
  /**
   * Render the form in a compact card-style frame. Used inside InsuraBot;
   * the standalone /quote-enroll page renders it borderless inside its own
   * card wrapper.
   */
  framed?: boolean;
  /**
   * Optional close affordance — when provided, an X button shows in the
   * header that calls this. Useful for chat cards that want a dismiss.
   */
  onDismiss?: () => void;
  /**
   * Re-quote flow (Quote history): auto-submit once on mount when the
   * preset is complete, so the agent lands straight on fresh results with
   * Apply buttons instead of re-driving the form. If required fields are
   * missing (e.g. lead has no height), the form stays interactive and the
   * "Still needed" line explains what to fill. Pair with a `key` on the
   * component so each history click remounts.
   */
  autoRun?: boolean;
}

const TERM_FACE_CHIPS = [100_000, 250_000, 500_000, 1_000_000];
const FE_FACE_CHIPS = [10_000, 15_000, 20_000, 25_000];
const TERM_LENGTHS = [10, 15, 20, 25, 30];

/**
 * The full PHC condition set per Techficient's 2026-06-24 `PreunderwritingRequestModel`.
 *
 * Each condition's `key` is the payload field name we send (the doc's flat
 * `conditionsXxx` / lifestyle field names). Grouped into sections so an agent
 * can find what they're looking for without scanning a wall of checkboxes —
 * every "yes" tightens the rate the carrier returns. Anything left unchecked
 * is omitted from the payload (carrier treats it as no condition).
 *
 * Notes on Term vs FE: some conditions are tracked separately for FE in the
 * doc (the `conditionsDowngradeXxx` family — COPD, emphysema, kidney, liver
 * etc.). For agents we surface the single human label; the payload builder
 * sends BOTH the Term and FE variant when applicable so the rating engine
 * uses whichever fits the productTypeFilter.
 */
const PHC_CONDITION_GROUPS: Array<{
  title: string;
  conditions: Array<{ key: string; label: string }>;
}> = [
  {
    title: "Heart & Stroke",
    conditions: [
      { key: "conditionsHypertension", label: "High blood pressure" },
      { key: "conditionsCholesterol", label: "High cholesterol" },
      { key: "conditionsHeartAttack", label: "Heart attack" },
      { key: "conditionsAngioplastyStent", label: "Angioplasty / stent" },
      { key: "conditionsAtrialFibrillation", label: "Atrial fibrillation (AFib)" },
      { key: "conditionsHeartDisease", label: "Heart disease / CAD" },
      { key: "conditionsTia", label: "TIA / mini-stroke" },
    ],
  },
  {
    title: "Metabolic & Endocrine",
    conditions: [
      { key: "conditionsDiabetes", label: "Diabetes" },
      { key: "conditionsAutoimmune", label: "Autoimmune condition" },
    ],
  },
  {
    title: "Respiratory",
    conditions: [
      { key: "conditionsAsthma", label: "Asthma" },
      { key: "conditionsSleepApnea", label: "Sleep apnea" },
      { key: "conditionsDowngradeCopd", label: "COPD" },
      { key: "conditionsDowngradeEmphysema", label: "Emphysema" },
      { key: "conditionsDowngradeBronchitis", label: "Chronic bronchitis" },
    ],
  },
  {
    title: "Mental Health",
    conditions: [
      { key: "conditionsDepression", label: "Depression" },
      { key: "conditionsAnxiety", label: "Anxiety" },
      { key: "conditionsBipolar", label: "Bipolar" },
      { key: "conditionsPtsd", label: "PTSD" },
      { key: "conditionsAdd", label: "ADD / ADHD" },
      { key: "conditionsSchizophrenia", label: "Schizophrenia" },
      { key: "conditionsSelfHarm", label: "Self-harm history" },
    ],
  },
  {
    title: "Cancer & Organ",
    conditions: [
      { key: "conditionsCancer", label: "Cancer (excl. non-melanoma skin)" },
      { key: "conditionsDowngradeKidney", label: "Kidney disease" },
      { key: "conditionsDowngradeLiver", label: "Liver disease" },
      { key: "conditionsDowngradeHepatitis", label: "Hepatitis" },
    ],
  },
  {
    title: "GI",
    conditions: [
      { key: "conditionsColitisChrons", label: "Colitis / Crohn's" },
      { key: "conditionsGerd", label: "GERD" },
    ],
  },
  {
    title: "Substance Use",
    conditions: [
      { key: "alcoholAbuse", label: "Alcohol abuse history" },
      { key: "drugAbuse", label: "Drug abuse history" },
    ],
  },
  {
    title: "Family History",
    conditions: [
      {
        key: "conditionsFamilyHxParents",
        label: "Parent diagnosed (heart, stroke, cancer, diabetes ≤ age 70)",
      },
      {
        key: "conditionsFamilyHxSiblings",
        label: "Sibling diagnosed (heart, stroke, cancer, diabetes ≤ age 50)",
      },
    ],
  },
  {
    title: "Care",
    conditions: [
      { key: "conditionsCare", label: "Receiving care / assistance" },
    ],
  },
];

/**
 * Flat list of every condition key — used by the payload builder to walk the
 * agent's checkbox state and emit `{[key]: true}` for each checked one.
 */
const ALL_PHC_KEYS = PHC_CONDITION_GROUPS.flatMap((g) =>
  g.conditions.map((c) => c.key),
);

const DUI_FIELD_KEYS = {
  dui: "drivingConvictionDui",
  suspension: "drivingConvictionSuspension",
  violations: "drivingConvictionViolations",
} as const;
const CRIMINAL_FIELD_KEY = "criminalHistory";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

/**
 * Agent-facing multi-carrier quote form. Designed to be the same surface in
 * two places — the /quote-enroll page (rendered inline after a lead is picked)
 * and inside InsuraBot (as an inline card the AI can pre-fill). All required
 * fields fit above-the-fold; health questions live in a collapsible section
 * so the page doesn't intimidate.
 *
 * The form owns the API call — submit hits `techficientService.runQuote` and
 * bubbles the result up via `onQuoted`. Errors surface in the form itself; no
 * toast.
 */
export function QuoteForm({
  preset = {},
  onQuoted,
  framed = false,
  onDismiss,
  autoRun = false,
}: QuoteFormProps) {
  // ─── form state ───
  // When quoting off a lead, the name comes from the lead. When quoting
  // "fresh" (no lead), the agent must say who they're quoting for — a quote
  // with no client is an orphan you can't enroll. Captured here and required
  // in that case.
  const hasLead = !!preset.leadId;
  const [clientName, setClientName] = useState<string>(preset.leadName || "");
  const [coverageType, setCoverageType] = useState<"Term" | "FinalExpense">(
    preset.coverageType || "Term",
  );
  const [faceAmount, setFaceAmount] = useState<number | "">(
    preset.faceAmount ?? (preset.coverageType === "FinalExpense" ? 15_000 : 250_000),
  );
  const [termYears, setTermYears] = useState<number>(preset.termYears ?? 20);
  const [birthDate, setBirthDate] = useState<string>(preset.birthDate || "");
  const [gender, setGender] = useState<"M" | "F" | "">(preset.gender || "");
  const [state, setState] = useState<string>(preset.state || "");
  const [zipCode, setZipCode] = useState<string>(preset.zipCode || "");
  const [heightFt, setHeightFt] = useState<number | "">(
    preset.heightInches != null ? Math.floor(preset.heightInches / 12) : "",
  );
  const [heightIn, setHeightIn] = useState<number | "">(
    preset.heightInches != null ? preset.heightInches % 12 : "",
  );
  // Single smart height input. Displays 5’10” style; accepts anything an
  // agent naturally types — 5'10, 5 10, 5-10, 510, 5ft10, or bare total
  // inches (70) — and normalizes into heightFt/heightIn, which the payload
  // builder already converts to the total-inches format Techficient wants.
  const [heightText, setHeightText] = useState<string>(
    preset.heightInches != null
      ? `${Math.floor(preset.heightInches / 12)}’${preset.heightInches % 12}”`
      : "",
  );
  const parseHeight = (raw: string): { ft: number; inch: number } | null => {
    const s = raw.trim().replace(/[’‘]/g, "'").replace(/[”“]/g, '"');
    if (!s) return null;
    // 5'10, 5' 10", 5 ft 10, 5-10, 5 10
    let m = s.match(/^(\d)\s*(?:'|ft|feet|-|\s)\s*(\d{1,2})?\s*(?:"|in|inches)?$/i);
    if (m) {
      const ft = Number(m[1]);
      const inch = m[2] != null ? Number(m[2]) : 0;
      if (ft >= 3 && ft <= 8 && inch >= 0 && inch <= 11) return { ft, inch };
      return null;
    }
    // Pure digits: "510" → 5'10". Two digits read as feet+inch ("53" →
    // 5'3" — that's what an agent means, NOT 53 total inches), falling
    // back to total inches only when the feet digit is implausible
    // ("96" → 8'0"). One digit = feet.
    if (/^\d{1,3}$/.test(s)) {
      const n = Number(s);
      if (s.length === 3) {
        const ft = Number(s[0]);
        const inch = Number(s.slice(1));
        if (ft >= 3 && ft <= 8 && inch <= 11) return { ft, inch };
        return null;
      }
      if (s.length === 2) {
        const ft = Number(s[0]);
        const inch = Number(s[1]);
        if (ft >= 3 && ft <= 8) return { ft, inch };
        if (n >= 36 && n <= 96) return { ft: Math.floor(n / 12), inch: n % 12 };
        return null;
      }
      if (n >= 3 && n <= 8) return { ft: n, inch: 0 };
    }
    return null;
  };
  const onHeightChange = (raw: string) => {
    // Numbers (plus the height punctuation) only — no letters.
    const cleaned = raw.replace(/[^\d'"’”\s-]/g, "");
    setHeightText(cleaned);
    const parsed = parseHeight(cleaned);
    if (parsed) {
      setHeightFt(parsed.ft);
      setHeightIn(parsed.inch);
    } else {
      setHeightFt("");
      setHeightIn("");
    }
  };
  const onHeightBlur = () => {
    const parsed = parseHeight(heightText);
    if (parsed) setHeightText(`${parsed.ft}’${parsed.inch}”`);
  };
  const [weightLbs, setWeightLbs] = useState<number | "">(preset.weightPounds ?? "");
  // Tobacco / marijuana per 2026-06-24 schema: tri-state enum strings, not
  // booleans. `null` = "agent didn't answer" (omitted from payload entirely),
  // strings get sent verbatim as `tobacco`/`marijuana` enum values.
  // Re-quote flow: `preset.phcDefaults` is the stored preUnderwritingRequest
  // from the original run (wire-format — the exact object buildPhcRequest
  // produced), so history re-quotes reproduce the ORIGINAL health answers.
  const phcDefaults: Record<string, any> = preset.phcDefaults || {};
  const [tobaccoStatus, setTobaccoStatus] = useState<
    "never" | "former" | "current" | null
  >(
    phcDefaults.tobacco ??
      (preset.tobaccoUse === true
        ? "current"
        : preset.tobaccoUse === false
          ? "never"
          : null),
  );
  const [marijuanaStatus, setMarijuanaStatus] = useState<
    "never" | "former" | "current" | null
  >(phcDefaults.marijuana ?? null);

  // The full Techficient PHC question set. Keyed by the wire field name
  // (`conditionsXxx`, `alcoholAbuse`, etc.) so the payload builder can splat
  // the object straight into the request. Every "yes" tightens the rate band
  // the carrier returns. Anything not toggled is omitted (carrier treats it
  // as "no condition").
  const [conditions, setConditions] = useState<Record<string, boolean>>(() => {
    const seed: Record<string, boolean> = {};
    for (const k of ALL_PHC_KEYS) if (phcDefaults[k] === true) seed[k] = true;
    return seed;
  });
  const [duiHistory, setDuiHistory] = useState<boolean | null>(
    typeof phcDefaults[DUI_FIELD_KEYS.dui] === "boolean"
      ? phcDefaults[DUI_FIELD_KEYS.dui]
      : null,
  );
  const [licenseSuspended, setLicenseSuspended] = useState<boolean | null>(
    typeof phcDefaults[DUI_FIELD_KEYS.suspension] === "boolean"
      ? phcDefaults[DUI_FIELD_KEYS.suspension]
      : null,
  );
  const [drivingViolations, setDrivingViolations] = useState<boolean | null>(
    typeof phcDefaults[DUI_FIELD_KEYS.violations] === "boolean"
      ? phcDefaults[DUI_FIELD_KEYS.violations]
      : null,
  );
  const [criminalHistory, setCriminalHistory] = useState<boolean | null>(
    typeof phcDefaults[CRIMINAL_FIELD_KEY] === "boolean"
      ? phcDefaults[CRIMINAL_FIELD_KEY]
      : null,
  );
  // Height/weight moved OUT to the always-visible Location & build row, so
  // this section is genuinely optional again. Open only when there are
  // restored/preset health answers to review — the agent should SEE what
  // the rating is based on, never have it silently applied.
  const [healthOpen, setHealthOpen] = useState(
    preset.tobaccoUse != null || preset.phcDefaults != null,
  );

  // Advanced section — Techficient also accepts payment frequency overrides
  // (Monthly is the default but for Annual / Quarterly / Semi-annual quotes
  // the carrier math changes) and a rider menu (ADB / WOP / Child).
  const [mode, setMode] = useState<
    "Monthly" | "Quarterly" | "Semi-annual" | "Annual"
  >(preset.mode || "Monthly");
  const [adbRider, setAdbRider] = useState<boolean>(!!preset.adbRider);
  const [wopRider, setWopRider] = useState<boolean>(!!preset.wopRider);
  const [childRider, setChildRider] = useState<"ZERO" | "FIVE" | "TEN">(
    preset.childRider || "ZERO",
  );
  const [advancedOpen, setAdvancedOpen] = useState(
    !!preset.mode || !!preset.adbRider || !!preset.wopRider ||
      (preset.childRider && preset.childRider !== "ZERO"),
  );

  // ─── async state ───
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<TechficientRunQuoteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bulletproof fallback: if the Techficient quote comes back 403 "feature not
  // enabled" (the agent's agency isn't actually entitled to Quote & Enroll —
  // e.g. a flag race), swap this whole form for the Compulife form so the
  // agent lands on a working quote instead of a dead-end error. Same UX,
  // Compulife-backed — exactly the "same form for both" behavior.
  const [useCompulifeFallback, setUseCompulifeFallback] = useState(false);

  // Lead-less run → ask BEFORE the quote fires whether to create a CRM lead
  // for this person. "Create" mints/attaches the lead right after the quote
  // returns (backend dedupes against the agent's book and never overrides
  // existing data), so the result tiles' Apply buttons work one-click.
  // "Skip" quotes without a lead — the Apply-time confirm still catches it.
  const [runLeadPrompt, setRunLeadPrompt] = useState(false);
  const [runLeadChoice, setRunLeadChoice] = useState<"create" | "skip" | null>(
    null,
  );
  const [ensuredLeadId, setEnsuredLeadId] = useState<string | null>(null);
  const [leadNotice, setLeadNotice] = useState<string | null>(null);

  // A different person typed in → the earlier create/skip answer no longer
  // applies; ask again on the next run.
  useEffect(() => {
    setRunLeadChoice(null);
    setEnsuredLeadId(null);
    setLeadNotice(null);
  }, [clientName, birthDate]);

  // If the preset changes (parent picks a different lead), re-seed.
  useEffect(() => {
    setClientName(preset.leadName || "");
    setBirthDate(preset.birthDate || "");
    setGender(preset.gender || "");
    setState(preset.state || "");
    setZipCode(preset.zipCode || "");
    if (preset.heightInches != null) {
      setHeightFt(Math.floor(preset.heightInches / 12));
      setHeightIn(preset.heightInches % 12);
      setHeightText(
        `${Math.floor(preset.heightInches / 12)}’${preset.heightInches % 12}”`,
      );
    }
    if (preset.weightPounds != null) setWeightLbs(preset.weightPounds);
    if (preset.tobaccoUse === true) setTobaccoStatus("current");
    else if (preset.tobaccoUse === false) setTobaccoStatus("never");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset.leadId, preset.birthDate, preset.gender, preset.state]);

  // Whenever the agent flips Term ↔ FinalExpense, reset face to a sane default
  // for the new type so they don't end up quoting $250K in funeral coverage.
  useEffect(() => {
    if (coverageType === "FinalExpense" && Number(faceAmount) > 50_000) {
      setFaceAmount(15_000);
    } else if (coverageType === "Term" && Number(faceAmount) < 50_000) {
      setFaceAmount(250_000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverageType]);

  // ─── validation ───
  const missing = useMemo(() => {
    const m: string[] = [];
    // No lead on file → the agent must name who they're quoting for.
    if (!hasLead && !clientName.trim()) m.push("Client name");
    if (!faceAmount || Number(faceAmount) <= 0) m.push("Coverage amount");
    if (!birthDate) m.push("Date of birth");
    if (!gender) m.push("Gender");
    if (!state) m.push("State");
    if (!/^\d{5}$/.test(zipCode)) m.push("ZIP code");
    // Height + weight are required — the personalized-health-class engine
    // can't rate without build data, and quotes silently degrade to generic
    // table rates. Surface them in the "Still needed" line instead.
    const totalInches = (Number(heightFt) || 0) * 12 + (Number(heightIn) || 0);
    if (totalInches <= 0) m.push("Height");
    if (!weightLbs || Number(weightLbs) <= 0) m.push("Weight");
    if (coverageType === "Term" && !termYears) m.push("Term length");
    return m;
  }, [hasLead, clientName, faceAmount, birthDate, gender, state, zipCode, heightFt, heightIn, weightLbs, coverageType, termYears]);

  const canSubmit = missing.length === 0 && !submitting;

  // ─── submit ───
  const runQuote = async (leadChoice?: "create" | "skip") => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    setLeadNotice(null);

    const totalInches =
      (Number(heightFt) || 0) * 12 + (Number(heightIn) || 0);

    // Build the Techficient payload using the 2026-06-24 schema's flat
    // `preUnderwritingRequest` object. Omitted fields = "agent didn't say",
    // not "no" — Techficient treats absence and `false` the same for booleans
    // but we'd rather pass clean omission so the carrier can apply its own
    // defaults if it chooses.
    const phc = buildPhcRequest({
      heightInInches: totalInches > 0 ? totalInches : undefined,
      weight: weightLbs ? Number(weightLbs) : undefined,
      tobacco: tobaccoStatus,
      marijuana: marijuanaStatus,
      conditions,
      duiHistory,
      licenseSuspended,
      drivingViolations,
      criminalHistory,
    });

    const request: TechficientQuoteRequestPayload = {
      amount: Number(faceAmount),
      class: "PHC",
      term: coverageType === "Term" ? termYears : null,
      gender: gender as "M" | "F",
      birthDate,
      state,
      adbRider,
      wopRider,
      childRider,
      productTypeFilter: coverageType,
      mode,
      ...(phc ? { preUnderwritingRequest: phc } : {}),
    } as TechficientQuoteRequestPayload;

    try {
      const response = await techficientService.runQuote({
        request,
        leadId: preset.leadId,
        ...(clientName.trim() ? { clientName: clientName.trim() } : {}),
        // Stored server-side so Quote-history re-quotes can prefill it.
        ...(zipCode ? { zipCode } : {}),
      });
      setResult(response);
      onQuoted?.({ request, response });

      // Agent said "create the lead" up front — do it now that the quote's
      // stored inputs exist server-side. Dedupe/attach happens on the
      // backend; a failure here never blocks the results (Apply's own
      // confirm flow remains as the fallback).
      const choice = leadChoice ?? runLeadChoice;
      if (!preset.leadId && choice === "create") {
        try {
          const ensured = await techficientService.ensureQuoteLead(
            response.quoteRequest.techficientQuoteRequestId,
          );
          setEnsuredLeadId(ensured.leadId);
          setLeadNotice(
            ensured.created
              ? `Lead created for ${clientName.trim() || "this applicant"} — Apply on any carrier below runs against it.`
              : `Matched an existing lead — no duplicate created. Apply runs against that lead.`,
          );
        } catch {
          setLeadNotice(
            "Couldn't create the lead just now — you can still create it when you hit Apply.",
          );
        }
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const raw = String(
        e?.response?.data?.message ||
          e?.message ||
          "Couldn't run quote — try again.",
      );
      const isFeatureError =
        status === 403 ||
        /quoteandenroll|feature .* not enabled|not enabled for this agency/i.test(
          raw,
        );
      // The agency isn't actually entitled to the enrollable engine (flag off
      // / flag race). Don't dead-end — swap to the Compulife form so the agent
      // gets a working quote. Requires a leadId (Compulife runs against a lead).
      if (isFeatureError && preset.leadId) {
        setUseCompulifeFallback(true);
        setError(null);
        return;
      }
      const friendly = isFeatureError
        ? "Quoting isn't available for this account. Try refreshing, or switch to the right agency."
        : // Scrub any vendor mention — the enrollment engine is white-labeled.
          raw.replace(/techficient|techf\.com/gi, "carrier");
      setError(friendly);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // No lead attached and the agent hasn't answered create-vs-skip for this
    // person yet → pause on the consent panel instead of firing the quote.
    if (!hasLead && runLeadChoice === null && missing.length === 0 && !submitting) {
      setRunLeadPrompt(true);
      return;
    }
    void runQuote();
  };

  // Re-quote flow: fire once on mount when the preset came in complete
  // (Quote history hands us the past quote's parameters + lead data). If
  // fields are missing the form stays interactive and "Still needed"
  // explains — never auto-run a half-filled quote.
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (!autoRun || autoRanRef.current) return;
    if (missing.length > 0 || submitting) return;
    autoRanRef.current = true;
    void runQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, missing.length]);

  // ─── render ───
  const isFE = coverageType === "FinalExpense";
  const faceChips = isFE ? FE_FACE_CHIPS : TERM_FACE_CHIPS;

  // Fallback: the agency isn't entitled to the enrollable engine — render the
  // Compulife form instead (same UX, works for everyone). Synthesize the lead
  // shape CompulifeQuoteForm reads from what we already have in the preset.
  if (useCompulifeFallback && preset.leadId) {
    const totalInches =
      (Number(heightFt) || 0) * 12 + (Number(heightIn) || 0);
    const fallbackLead = {
      id: preset.leadId,
      name: clientName.trim() || preset.leadName || undefined,
      dob: birthDate || undefined,
      gender: gender || undefined,
      state: state || undefined,
      zipCode: zipCode || undefined,
      height: totalInches > 0 ? totalInches : undefined,
      weight: weightLbs ? Number(weightLbs) : undefined,
    } as any;
    return (
      <div className={framed ? "rounded-xl border bg-white shadow-sm p-5" : ""}>
        <CompulifeQuoteForm leadId={preset.leadId} lead={fallbackLead} />
        {/* CompulifeQuoteForm relies on a parent for results — render them
            here so the fallback path shows the carrier boxes too. */}
        <CompulifeQuoteResults leadId={preset.leadId} />
      </div>
    );
  }

  return (
    <div className={framed ? "rounded-xl border bg-white shadow-sm" : ""}>
      <form onSubmit={handleSubmit} className={framed ? "p-5" : ""}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
              Quote request
            </p>
            <h3 className="text-base font-semibold text-gray-900 mt-0.5">
              {clientName.trim() ? (
                <>
                  Quoting for{" "}
                  <span className="text-primary">{clientName.trim()}</span>
                </>
              ) : (
                "New quote"
              )}
            </h3>
          </div>
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Who are you quoting for? — only when there's no lead on file. A
            quote you can't attribute to a person can't be enrolled, so this
            is required before Run. When quoting off a lead we skip it (the
            name comes from the lead and is shown in the header above). */}
        {!hasLead && (
          <Section icon={User} label="Who are you quoting for?">
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Client's full name"
              autoComplete="off"
              className={`w-full h-10 px-3 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${
                !clientName.trim()
                  ? "border-rose-300 bg-rose-50/40"
                  : "border-gray-200"
              }`}
            />
          </Section>
        )}

        {/* Coverage type — big radio cards */}
        <Section icon={Shield} label="Coverage type">
          <div className="grid grid-cols-2 gap-2">
            <TypePill
              active={coverageType === "Term"}
              label="Term"
              hint="10–30 yrs · larger coverage"
              onClick={() => setCoverageType("Term")}
            />
            <TypePill
              active={coverageType === "FinalExpense"}
              label="Final Expense"
              hint="Whole life · burial coverage"
              onClick={() => setCoverageType("FinalExpense")}
            />
          </div>
        </Section>

        {/* Amount + term row */}
        <Section icon={Coins} label="Coverage amount">
          <div className="flex flex-wrap gap-2 mb-2">
            {faceChips.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => setFaceAmount(chip)}
                className={`px-3 py-1 rounded-full border text-xs font-medium transition ${
                  Number(faceAmount) === chip
                    ? "bg-primary text-white border-primary"
                    : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                }`}
              >
                ${chip >= 1_000_000 ? `${chip / 1_000_000}M` : `${chip / 1_000}K`}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">$</span>
            <Input
              type="number"
              inputMode="numeric"
              value={faceAmount}
              onChange={(e) =>
                setFaceAmount(e.target.value === "" ? "" : Number(e.target.value))
              }
              className="h-9"
              min={0}
              step={1000}
            />
            {!isFE && (
              <select
                value={termYears}
                onChange={(e) => setTermYears(Number(e.target.value))}
                className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-primary focus:outline-none"
              >
                {TERM_LENGTHS.map((y) => (
                  <option key={y} value={y}>
                    {y} years
                  </option>
                ))}
              </select>
            )}
          </div>
        </Section>

        {/* Demographics row */}
        <Section icon={User} label="About the applicant">
          <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_110px_100px] gap-3">
            <Field label="Date of birth *">
              <div className="relative">
                <Calendar className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                <Input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className={`h-9 pl-7 ${!birthDate ? "border-rose-300 bg-rose-50/40" : ""}`}
                  max={new Date().toISOString().slice(0, 10)}
                />
              </div>
            </Field>
            <Field label="Gender *">
              <div
                className={`grid grid-cols-2 gap-2 rounded-md ${
                  !gender ? "ring-1 ring-rose-300 bg-rose-50/40 p-0.5" : ""
                }`}
              >
                <GenderPill
                  active={gender === "M"}
                  label="Male"
                  onClick={() => setGender("M")}
                />
                <GenderPill
                  active={gender === "F"}
                  label="Female"
                  onClick={() => setGender("F")}
                />
              </div>
            </Field>
            <Field label="Height *">
              <Input
                type="text"
                inputMode="numeric"
                value={heightText}
                onChange={(e) => onHeightChange(e.target.value)}
                onBlur={onHeightBlur}
                className={`h-9 ${
                  (Number(heightFt) || 0) * 12 + (Number(heightIn) || 0) <= 0
                    ? "border-rose-300 bg-rose-50/40"
                    : ""
                }`}
              />
            </Field>
            <Field label="Weight (lbs) *">
              <Input
                type="number"
                inputMode="numeric"
                value={weightLbs}
                min={50}
                max={600}
                onChange={(e) =>
                  setWeightLbs(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                className={`h-9 ${
                  !weightLbs || Number(weightLbs) <= 0
                    ? "border-rose-300 bg-rose-50/40"
                    : ""
                }`}
              />
            </Field>
          </div>
        </Section>

        {/* Location row */}
        <Section icon={MapPin} label="Location">
          <div className="grid grid-cols-[1fr_140px] gap-3">
            <Field label="State *">
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className={`h-9 w-full rounded-md border bg-white px-2 text-sm focus:border-primary focus:outline-none ${
                  !state ? "border-rose-300 bg-rose-50/40" : "border-gray-200"
                }`}
              >
                <option value="">Select…</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="ZIP *">
              <Input
                type="text"
                inputMode="numeric"
                pattern="\d{5}"
                maxLength={5}
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.replace(/\D/g, ""))}
                className={`h-9 ${!/^\d{5}$/.test(zipCode) ? "border-rose-300 bg-rose-50/40" : ""}`}
                placeholder="—"
              />
            </Field>
          </div>
        </Section>

        {/* Health (collapsible — agents skip unless they want better rates) */}
        <button
          type="button"
          onClick={() => setHealthOpen((v) => !v)}
          className="mt-4 w-full flex items-center justify-between rounded-md border border-dashed border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <span className="inline-flex items-center gap-2">
            <Heart className="h-3.5 w-3.5 text-rose-400" />
            Health questions
            <span className="text-[10px] text-gray-400 font-normal">
              (optional · improves rates)
            </span>
          </span>
          {healthOpen ? (
            <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
          )}
        </button>

        {healthOpen && (
          <div className="mt-3 rounded-md bg-gray-50/60 p-3 space-y-3 border border-gray-100">
            <Field label="Tobacco use">
              <div className="grid grid-cols-3 gap-2">
                <TogglePill
                  active={tobaccoStatus === "never"}
                  label="Never"
                  onClick={() => setTobaccoStatus("never")}
                />
                <TogglePill
                  active={tobaccoStatus === "former"}
                  label="Former"
                  onClick={() => setTobaccoStatus("former")}
                />
                <TogglePill
                  active={tobaccoStatus === "current"}
                  label="Current"
                  onClick={() => setTobaccoStatus("current")}
                />
              </div>
            </Field>
            <Field label="Marijuana use">
              <div className="grid grid-cols-3 gap-2">
                <TogglePill
                  active={marijuanaStatus === "never"}
                  label="Never"
                  onClick={() => setMarijuanaStatus("never")}
                />
                <TogglePill
                  active={marijuanaStatus === "former"}
                  label="Former"
                  onClick={() => setMarijuanaStatus("former")}
                />
                <TogglePill
                  active={marijuanaStatus === "current"}
                  label="Current"
                  onClick={() => setMarijuanaStatus("current")}
                />
              </div>
            </Field>

            {/* Grouped malady checklist — the full Techficient 2026-06-24
                PreUnderwritingRequestModel. Sections keep the wall of
                checkboxes scannable; agents who never had a Final Expense
                client will still find "Heart attack" instantly. */}
            {PHC_CONDITION_GROUPS.map((group) => (
              <div key={group.title}>
                <p className="text-[10px] uppercase tracking-wide text-gray-500 font-bold mb-1.5">
                  {group.title}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {group.conditions.map((c) => (
                    <ConditionChip
                      key={c.key}
                      label={c.label}
                      active={!!conditions[c.key]}
                      onClick={() =>
                        setConditions((prev) => ({
                          ...prev,
                          [c.key]: !prev[c.key],
                        }))
                      }
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* Lifestyle / legal — DUI + criminal record. Both Term and FE
                rating engines downgrade for these, so they belong in the
                pre-underwriting payload not in advanced. */}
            <div>
              <p className="text-[10px] uppercase tracking-wide text-gray-500 font-bold mb-1.5">
                Driving &amp; Legal
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                <ConditionChip
                  label="DUI / DWI in last 10 years"
                  active={duiHistory === true}
                  onClick={() => setDuiHistory(duiHistory === true ? false : true)}
                />
                <ConditionChip
                  label="License suspended / revoked"
                  active={licenseSuspended === true}
                  onClick={() =>
                    setLicenseSuspended(licenseSuspended === true ? false : true)
                  }
                />
                <ConditionChip
                  label="2+ moving violations (last 2 yrs)"
                  active={drivingViolations === true}
                  onClick={() =>
                    setDrivingViolations(drivingViolations === true ? false : true)
                  }
                />
                <ConditionChip
                  label="Felony / on parole / probation"
                  active={criminalHistory === true}
                  onClick={() =>
                    setCriminalHistory(criminalHistory === true ? false : true)
                  }
                />
              </div>
            </div>
          </div>
        )}

        {/* Advanced — mode + riders */}
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="mt-3 w-full flex items-center justify-between rounded-md border border-dashed border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <span className="inline-flex items-center gap-2">
            <Settings2 className="h-3.5 w-3.5 text-gray-400" />
            Advanced
            <span className="text-[10px] text-gray-400 font-normal">
              (payment frequency, riders)
            </span>
          </span>
          {advancedOpen ? (
            <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
          )}
        </button>

        {advancedOpen && (
          <div className="mt-3 rounded-md bg-gray-50/60 p-3 space-y-3 border border-gray-100">
            <Field label="Payment frequency">
              <div className="grid grid-cols-4 gap-1.5">
                {(["Monthly", "Quarterly", "Semi-annual", "Annual"] as const).map(
                  (m) => (
                    <TogglePill
                      key={m}
                      active={mode === m}
                      label={m}
                      onClick={() => setMode(m)}
                    />
                  ),
                )}
              </div>
            </Field>
            <Field label="Riders">
              <div className="grid grid-cols-2 gap-1.5">
                <TogglePill
                  active={adbRider}
                  label="Accidental Death"
                  onClick={() => setAdbRider((v) => !v)}
                />
                <TogglePill
                  active={wopRider}
                  label="Waiver of Premium"
                  onClick={() => setWopRider((v) => !v)}
                />
              </div>
            </Field>
            <Field label="Child rider">
              <div className="grid grid-cols-3 gap-1.5">
                {(
                  [
                    { v: "ZERO", label: "None" },
                    { v: "FIVE", label: "$5K" },
                    { v: "TEN", label: "$10K" },
                  ] as const
                ).map((opt) => (
                  <TogglePill
                    key={opt.v}
                    active={childRider === opt.v}
                    label={opt.label}
                    onClick={() => setChildRider(opt.v)}
                  />
                ))}
              </div>
            </Field>
          </div>
        )}

        {/* Submit */}
        <div className="mt-5 flex items-center justify-between gap-3">
          {missing.length > 0 ? (
            <p className="text-[11px] text-amber-600 inline-flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Still needed: {missing.join(", ")}
            </p>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            disabled={!canSubmit}
            className="min-w-[160px]"
          >
            {submitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Running…
              </>
            ) : (
              <>
                <QuoteBoltIcon className="h-4 w-4 mr-1.5" />
                Run multi-carrier quote
              </>
            )}
          </Button>
        </div>

        {/* Lead-less consent panel — appears when Run is pressed with no lead
            attached. The agent decides up front: create a CRM lead for this
            person (dedupe-checked, never overrides) or just quote. */}
        {runLeadPrompt && (
          <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-3 animate-in fade-in slide-in-from-top-1 duration-300">
            <p className="text-[12px] font-semibold text-blue-900">
              Create a lead for {clientName.trim() || "this applicant"}?
            </p>
            <p className="text-[11px] text-blue-800/80 mt-1 leading-relaxed">
              We&apos;ll first check your leads for an existing match — no
              duplicates get created, and nothing on an existing lead gets
              overwritten. If this quote is for a family member of a client,
              that&apos;s fine: they get their own lead. You can also just run
              the quote without one.
            </p>
            <p className="text-[11px] font-medium text-amber-700 mt-1.5 flex items-start gap-1">
              <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              Double-check the date of birth and state before continuing —
              they drive the rates and the application.
            </p>
            <p className="text-[11px] text-blue-900/70 mt-1.5 font-medium">
              {[
                clientName.trim(),
                birthDate ? `DOB ${birthDate}` : null,
                state || null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <button
                type="button"
                onClick={() => {
                  setRunLeadPrompt(false);
                  setRunLeadChoice("create");
                  void runQuote("create");
                }}
                className="text-[11px] font-semibold rounded-md px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700"
              >
                Create lead &amp; run quote
              </button>
              <button
                type="button"
                onClick={() => {
                  setRunLeadPrompt(false);
                  setRunLeadChoice("skip");
                  void runQuote("skip");
                }}
                className="text-[11px] font-semibold rounded-md px-3 py-1.5 bg-white text-blue-700 ring-1 ring-inset ring-blue-200 hover:bg-blue-100/60"
              >
                Just quote — no lead
              </button>
              <button
                type="button"
                onClick={() => setRunLeadPrompt(false)}
                className="text-[11px] font-medium rounded-md px-3 py-1.5 text-gray-600 hover:bg-white"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </form>

      {/* Results — render inline below the form once the carriers come back */}
      {result && leadNotice && (
        <div className="mt-1 mb-1 flex items-start gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-800">
          <User className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>{leadNotice}</span>
        </div>
      )}
      {result && (
        <QuoteResults
          response={result}
          leadId={preset.leadId ?? ensuredLeadId ?? null}
          coverageType={coverageType}
          termYears={termYears}
          faceAmount={Number(faceAmount)}
          state={state}
          applicantSummary={summarize({
            name: clientName.trim() || preset.leadName,
            coverageType,
            faceAmount: Number(faceAmount),
            termYears,
            state,
            birthDate,
          })}
        />
      )}
    </div>
  );
}

// ─────────────────────────── helpers ───────────────────────────

function Section({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-3">
      <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-2 inline-flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-gray-400" />
        {label}
      </p>
      {children}
    </div>
  );
}

/**
 * Custom "charged bolt" icon for the Run button — filled lightning bolt
 * ripping past speed lines. Original artwork (no external icon set), sized
 * and stroked to sit next to lucide icons without looking foreign.
 */
function QuoteBoltIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* speed lines trailing off the bolt */}
      <path
        d="M2.5 6.5h4.2M1.5 12h3.4M2.5 17.5h4.2"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        opacity="0.5"
      />
      {/* the bolt — filled, slightly canted forward for motion */}
      <path
        d="M15.2 1.8 8 13.1h4.3L10.4 22.2 18.6 10h-4.5l1.1-8.2Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[10px] text-gray-500 mb-0.5">{label}</span>
      {children}
    </label>
  );
}

function TypePill({
  active,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border px-3 py-2.5 transition ${
        active
          ? "border-primary bg-primary/5 ring-1 ring-primary/40"
          : "border-gray-200 bg-white hover:border-gray-300"
      }`}
    >
      <p className={`text-sm font-semibold ${active ? "text-primary" : "text-gray-900"}`}>
        {label}
      </p>
      <p className="text-[10px] text-gray-500 mt-0.5">{hint}</p>
    </button>
  );
}

function GenderPill({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 rounded-md border text-sm font-medium transition ${
        active
          ? "border-primary bg-primary/5 text-primary"
          : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
      }`}
    >
      {label}
    </button>
  );
}

function TogglePill({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 rounded-md border text-sm font-medium transition ${
        active
          ? "border-primary bg-primary/5 text-primary"
          : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
      }`}
    >
      {label}
    </button>
  );
}

function ConditionChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 rounded-md border text-[11px] font-medium transition px-2 text-left flex items-center gap-1.5 ${
        active
          ? "border-rose-300 bg-rose-50 text-rose-700"
          : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
      }`}
    >
      <span
        className={`h-3 w-3 rounded-sm border flex-shrink-0 flex items-center justify-center ${
          active ? "border-rose-500 bg-rose-500 text-white" : "border-gray-300"
        }`}
      >
        {active && (
          <svg viewBox="0 0 12 12" className="h-2.5 w-2.5">
            <path
              d="M2 6l3 3 5-6"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
          </svg>
        )}
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

/**
 * Build the 2026-06-24 `preUnderwritingRequest` payload Techficient consumes
 * on `quote/getquote` for PHC quotes.
 *
 * Schema is flat — `tobacco`/`marijuana` as enum strings, `heightInInches` +
 * `weight` for demographics, and every malady as a top-level `conditionsXxx`
 * boolean. Unset fields are omitted entirely so the carrier can apply its
 * own defaults instead of treating "no answer" as "no".
 *
 * Returns `null` when the agent didn't provide any PHC inputs — caller skips
 * the whole `preUnderwritingRequest` key in that case.
 */
function buildPhcRequest({
  heightInInches,
  weight,
  tobacco,
  marijuana,
  conditions,
  duiHistory,
  licenseSuspended,
  drivingViolations,
  criminalHistory,
}: {
  heightInInches?: number;
  weight?: number;
  tobacco: "never" | "former" | "current" | null;
  marijuana: "never" | "former" | "current" | null;
  conditions: Record<string, boolean>;
  duiHistory: boolean | null;
  licenseSuspended: boolean | null;
  drivingViolations: boolean | null;
  criminalHistory: boolean | null;
}): Record<string, any> | null {
  const out: Record<string, any> = {};

  if (heightInInches != null) out.heightInInches = heightInInches;
  if (weight != null) out.weight = weight;
  if (tobacco != null) out.tobacco = tobacco;
  if (marijuana != null) out.marijuana = marijuana;

  for (const k of ALL_PHC_KEYS) {
    if (conditions[k]) out[k] = true;
  }

  if (duiHistory != null) out[DUI_FIELD_KEYS.dui] = duiHistory;
  if (licenseSuspended != null) out[DUI_FIELD_KEYS.suspension] = licenseSuspended;
  if (drivingViolations != null) out[DUI_FIELD_KEYS.violations] = drivingViolations;
  if (criminalHistory != null) out[CRIMINAL_FIELD_KEY] = criminalHistory;

  return Object.keys(out).length === 0 ? null : out;
}

function summarize({
  name,
  coverageType,
  faceAmount,
  termYears,
  state,
  birthDate,
}: {
  name?: string | null;
  coverageType: "Term" | "FinalExpense";
  faceAmount: number;
  termYears: number;
  state: string;
  birthDate: string;
}): string {
  const age = birthDate
    ? Math.floor(
        (Date.parse(new Date().toISOString().slice(0, 10)) -
          Date.parse(birthDate)) /
          (365.25 * 24 * 3600 * 1000),
      )
    : null;
  const face = `$${faceAmount.toLocaleString()}`;
  const term =
    coverageType === "Term" ? `${termYears}-yr term` : "Final Expense";
  const parts = [
    name || "Applicant",
    age != null ? `age ${age}` : null,
    term,
    face,
    state,
  ].filter(Boolean);
  return parts.join(" · ");
}

// ─────────────────────── Results (inline carrier cards) ───────────────────────

import TechficientQuoteResultCard from "@/app/_module/components/insurabot-cards/TechficientQuoteResultCard";

function QuoteResults({
  response,
  leadId,
  coverageType,
  termYears,
  faceAmount,
  state,
  applicantSummary,
}: {
  response: TechficientRunQuoteResponse;
  leadId: string | null;
  coverageType: "Term" | "FinalExpense";
  termYears: number;
  faceAmount: number;
  state?: string;
  applicantSummary: string;
}) {
  // Zero carriers is a legitimate outcome — the applicant's coverage / product
  // / health details can knock out every appointed carrier. Show a clear
  // "no matches" state with next steps so it never reads as a silent failure.
  if (!response.results || response.results.length === 0) {
    return (
      <div className="border-t pt-4 mt-2 pb-2 px-1">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">
              No carriers returned a quote for these details
            </p>
            <p className="text-amber-800/80 mt-1 leading-relaxed">
              That usually means the coverage amount, product type, or health
              answers fall outside what your appointed carriers offer for this
              applicant. Try lowering the coverage amount, switching Term ↔
              Final Expense, or revisiting the health questions, then run it
              again.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="border-t pt-3 mt-2 pb-1 px-1">
      <TechficientQuoteResultCard
        data={{
          quoteRequestId: response.quoteRequest.techficientQuoteRequestId,
          leadId: leadId || undefined,
          applicantSummary,
          coverageType,
          termYears: coverageType === "Term" ? termYears : null,
          faceAmount,
          state,
          results: response.results.map((r) => ({
            quoteRequestResultId: r.techficientQuoteRequestResultId,
            carrier: r.carrierName,
            product: r.productName,
            monthlyPremium: r.monthlyPremium,
            annualPremium: r.annualPremium,
            healthClass: r.healthClassDisplayName,
            instant: r.isInstantIssue,
            supportsAppSubmit: r.supportsAppSubmit,
            // Trust the backend's classification — drop tickets must route
            // through direct-submit (no-tab popup), not the iframe path.
            submittableType:
              r.submittableType ||
              (r.supportsAppSubmit ? "full_eapp" : "quote_only"),
          })),
        }}
      />
    </div>
  );
}

export default QuoteForm;
